import React, { useEffect, useMemo, useState } from "react";
import { Card } from "@lightspeed/flame/Card";
import { Input } from "@lightspeed/flame/Input";
import Loader from "../Loader";
import { serverUrl } from "../../serverUrl";

const GENERAL_URL = `${serverUrl}/api/general`;
const pendingLoadRequests = new Map();

const OPERATIONS = {
  types: {
    load: "CUSTOMER_TYPES_GET",
    saveActive: "CUSTOMER_TYPE_ACTIVE_SAVE",
    saveColor: "CUSTOMER_TYPE_COLOR_SAVE",
    saveDiscount: "CUSTOMER_TYPE_DISCOUNT_SAVE",
  },
  customers: {
    load: "DISCOUNT_CUSTOMERS_GET",
    saveActive: "CUSTOMER_ACTIVE_SAVE",
    saveColor: "CUSTOMER_COLOR_SAVE",
    saveDiscount: "CUSTOMER_DISCOUNT_SAVE",
  },
};

const text = (value, fallback = "") => String(value ?? fallback).trim();

const first = (row, keys, fallback = "") => {
  for (const key of keys) {
    if (row?.[key] !== undefined && row?.[key] !== null) return row[key];
  }
  return fallback;
};

const activeValue = (value) => {
  if (typeof value === "boolean") return value;
  return ["1", "true", "yes", "y", "on", "active"].includes(String(value).toLowerCase());
};

const hexColor = (value) => /^#[0-9a-fA-F]{6}$/.test(text(value));

const rowsFromResponse = (data, mode) => {
  if (Array.isArray(data)) return data;
  if (mode === "types") {
    return data?.customer_types || data?.customerTypes || data?.types || data?.rows || data?.data || [];
  }
  return data?.customers || data?.rows || data?.data || data?.items || [];
};

const discountOptionsFromResponse = (data) => {
  const raw =
    data?.discount_codes ||
    data?.discount_codes_array ||
    data?.discountCodes ||
    data?.discounts ||
    data?.discount_code_list ||
    [];

  return (Array.isArray(raw) ? raw : [])
    .map((item) => {
      if (typeof item === "string" || typeof item === "number") {
        const code = text(item);
        return { code, label: code };
      }

      const code = text(first(item, ["discount_code", "discountCode", "discount_id", "discountId", "code", "id"]));
      const name = text(first(item, ["discount_name", "discountName", "description", "name", "label"], code));
      const percent = text(first(item, ["discountPercentage", "discount_percentage", "percentage"]));
      const label = [code, name && name !== code ? name : "", percent ? `${percent}%` : ""].filter(Boolean).join(" - ");
      return { code, label };
    })
    .filter((item) => item.code);
};

const normalizeTypeRow = (row, companyId, index) => {
  const typeId = text(first(row, ["customer_type_id", "customerTypeId", "type_id", "typeId", "id", "value"]));
  const typeName = text(
    first(
      row,
      ["customer_type_name", "customerTypeName", "type_name", "typeName", "description", "name", "label"],
      typeId
    )
  );

  return {
    raw: row,
    rowKey: typeId || typeName || String(index),
    companyId: text(first(row, ["token_company_id", "tokenCompanyId", "company_id", "companyId"], companyId)),
    typeId,
    typeName,
    active: first(row, ["active", "is_active", "isActive"], ""),
    color: text(first(row, ["customer_type_color", "customerTypeColor", "color", "colour", "discount_color", "discountColor"])),
    discountCode: text(first(row, ["discount_code", "discountCode", "discount_id", "discountId"])),
  };
};

const normalizeCustomerRow = (row, companyId, index) => {
  const id = text(first(row, ["id", "customer_id", "qb_customer_id", "customerid", "customer_number", "value"], ""));
  const name = text(
    first(row, ["description", "customer_name", "name", "display_name", "fully_qualified_name", "label"], id)
  );

  return {
    raw: row,
    rowKey: id || name || String(index),
    companyId: text(first(row, ["token_company_id", "tokenCompanyId", "company_id", "companyId"], companyId)),
    id,
    name,
    active: first(row, ["active", "is_active", "isActive"], ""),
    color: text(first(row, ["customer_color", "customerColor", "color", "colour", "discount_color", "discountColor"])),
    discountCode: text(first(row, ["discount_code", "discountCode", "discount_id", "discountId"])),
  };
};

const CustomerDirectoryTable = ({ mode = "customers", companyId, areaId, direct = false, refreshKey = 0 }) => {
  const operation = OPERATIONS[mode] || OPERATIONS.customers;
  const normalizedCompanyId = text(companyId);
  const normalizedAreaId = direct ? "" : text(areaId);

  const [rows, setRows] = useState([]);
  const [discountOptions, setDiscountOptions] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState("");
  const [message, setMessage] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!normalizedCompanyId || normalizedCompanyId === "-1" || normalizedCompanyId === "0") return undefined;
    if (!direct && !normalizedAreaId) return undefined;

    let active = true;
    const payload = withAreaPayload({
      operation_id: operation.load,
      integration_name: "custom_k_discount",
      token_company_id: normalizedCompanyId,
    }, normalizedAreaId);

    const loadRows = async () => {
      setLoading(true);
      setError("");

      try {
        const data = await postGeneralLoadOnce(payload);
        if (!active) return;

        const rawRows = rowsFromResponse(data, mode);
        const normalizedRows = rawRows
          .map((row, index) =>
            mode === "types"
              ? normalizeTypeRow(row, normalizedCompanyId, index)
              : normalizeCustomerRow(row, normalizedCompanyId, index)
          )
          .filter((row) => (mode === "types" ? row.typeId || row.typeName : row.id || row.name));

        setRows(normalizedRows);
        setDiscountOptions(discountOptionsFromResponse(data));
      } catch (err) {
        if (!active) return;
        setRows([]);
        setDiscountOptions([]);
        setError(err.message || "Unable to load data");
      } finally {
        if (active) setLoading(false);
      }
    };

    loadRows();
    return () => {
      active = false;
    };
  }, [direct, mode, normalizedAreaId, normalizedCompanyId, operation.load, refreshKey]);

  const visibleRows = useMemo(() => {
    const query = search.toLowerCase();
    if (!query) return rows;
    return rows.filter((row) =>
      Object.values(row)
        .filter((value) => typeof value !== "object")
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [rows, search]);

  const showMessage = (type, textValue) => {
    setMessage({ type, text: textValue });
    window.setTimeout(() => setMessage(null), 2500);
  };

  const saveField = async (row, field, value) => {
    const rowKey = row.rowKey;
    const previousValue = row[field];
    const saveKey = `${rowKey}:${field}`;
    const nextValue = field === "color" ? text(value).toLowerCase() : value;

    if (previousValue === nextValue || (field === "color" && !hexColor(nextValue))) return;

    setRows((currentRows) =>
      currentRows.map((item) => (item.rowKey === rowKey ? { ...item, [field]: nextValue } : item))
    );
    setSavingKey(saveKey);
    setMessage(null);

    try {
      await postGeneral(buildSavePayload(row, field, nextValue));
      showMessage("success", "Saved");
    } catch (err) {
      setRows((currentRows) =>
        currentRows.map((item) => (item.rowKey === rowKey ? { ...item, [field]: previousValue } : item))
      );
      showMessage("error", err.message || "Save failed");
    } finally {
      setSavingKey("");
    }
  };

  const buildSavePayload = (row, field, value) => {
    const payload = {
      operation_id: operationForField(operation, field),
      integration_name: "custom_k_discount",
      token_company_id: normalizedCompanyId,
    };
    const payloadWithArea = withAreaPayload(payload, normalizedAreaId);

    if (field === "active") payloadWithArea.active = value ? 1 : 0;
    if (field === "discountCode") payloadWithArea.discount_code = value;
    if (field === "color" && mode === "types") payloadWithArea.customer_type_color = value;
    if (field === "color" && mode !== "types") payloadWithArea.color = value;

    if (mode === "types") {
      return {
        ...payloadWithArea,
        customer_type_id: row.typeId,
        customer_type_name: row.typeName,
      };
    }

    return {
      ...payloadWithArea,
      customer_id: row.id,
      customer_name: row.name,
    };
  };

  if (loading) return <Loader />;

  return (
    <Card style={{ margin: 16, padding: 16 }}>
      <div style={tableHeaderStyle}>
        <h2 style={{ margin: 0, fontSize: 18 }}>{mode === "types" ? "Customer Types" : "Customers"}</h2>
        <div style={{ width: 280 }}>
          <Input size="small" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search" />
        </div>
      </div>

      {error && <StatusMessage type="error" text={error} />}
      {message && <StatusMessage type={message.type} text={message.text} />}

      <div style={{ overflow: "auto" }}>
        <table style={tableStyle}>
          <colgroup>
            <col style={identityColStyle} />
            <col style={identityColStyle} />
            <col style={identityColStyle} />
            <col style={discountColStyle} />
            <col style={discountColStyle} />
            <col style={discountColStyle} />
          </colgroup>
          <thead>
            <tr>
              <th colSpan={3} style={identityGroupHeaderStyle}>
                {mode === "types" ? "R-SERIES CUSTOMER TYPES" : "R-SERIES CUSTOMERS"}
              </th>
              <th colSpan={3} style={discountGroupHeaderStyle}>
                K-SERIES DISCOUNT
              </th>
            </tr>
            <tr>
              <th style={identityHeaderStyle}>Company ID</th>
              {mode === "types" ? (
                <>
                  <th style={identityHeaderStyle}>Customer Type ID</th>
                  <th style={identityHeaderStyle}>Customer Type Name</th>
                </>
              ) : (
                <>
                  <th style={identityHeaderStyle}>Customer ID</th>
                  <th style={identityHeaderStyle}>Customer Name</th>
                </>
              )}
              <th style={discountHeaderFirstStyle}>Active</th>
              <th style={discountHeaderStyle}>Color</th>
              <th style={discountHeaderStyle}>Discount Code</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => (
              <tr key={row.rowKey}>
                <td style={identityCellStyle}>{row.companyId || normalizedCompanyId}</td>
                {mode === "types" ? (
                  <>
                    <td style={identityCellStyle}>{row.typeId}</td>
                    <td style={identityCellStyle}>{row.typeName}</td>
                  </>
                ) : (
                  <>
                    <td style={identityCellStyle}>{row.id}</td>
                    <td style={identityCellStyle}>{row.name}</td>
                  </>
                )}
                <td style={discountCellFirstStyle}>
                  <input
                    type="checkbox"
                    checked={activeValue(row.active)}
                    disabled={savingKey === `${row.rowKey}:active`}
                    onChange={(event) => saveField(row, "active", event.target.checked)}
                    style={checkboxStyle}
                  />
                </td>
                <td style={discountCellStyle}>
                  <ColorInput
                    value={row.color}
                    disabled={savingKey === `${row.rowKey}:color`}
                    onSave={(value) => saveField(row, "color", value)}
                  />
                </td>
                <td style={discountCellStyle}>
                  <DiscountSelect
                    value={row.discountCode}
                    options={discountOptions}
                    disabled={savingKey === `${row.rowKey}:discountCode`}
                    onChange={(value) => saveField(row, "discountCode", value)}
                  />
                </td>
              </tr>
            ))}

            {!visibleRows.length && (
              <tr>
                <td style={cellStyle} colSpan={6}>
                  No records found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

const postGeneral = async (payload, signal) => {
  const response = await fetch(GENERAL_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    signal,
  });
  const data = await response.json().catch(() => ({}));

  if (!response.ok || data?.success === false || data?.ok === false || data?.error) {
    throw new Error(data?.message || data?.error || data?.error_message || `Request failed (${response.status})`);
  }

  return data;
};

const postGeneralLoadOnce = (payload) => {
  const key = JSON.stringify(payload);

  if (!pendingLoadRequests.has(key)) {
    pendingLoadRequests.set(
      key,
      postGeneral(payload).finally(() => {
        pendingLoadRequests.delete(key);
      })
    );
  }

  return pendingLoadRequests.get(key);
};

const withAreaPayload = (payload, areaId) => {
  if (!areaId || areaId === "0") return payload;

  return {
    ...payload,
    token_area_id: areaId,
    area_id: areaId,
  };
};

const operationForField = (operation, field) => {
  if (field === "active") return operation.saveActive;
  if (field === "color") return operation.saveColor;
  return operation.saveDiscount;
};

const StatusMessage = ({ type, text }) => (
  <div style={{ color: type === "success" ? "#15803d" : "#b91c1c", marginBottom: 12, fontWeight: 600 }}>
    {text}
  </div>
);

const ColorInput = ({ value, disabled, onSave }) => {
  const normalizedValue = hexColor(value) ? text(value).toLowerCase() : "#007b04";
  const [draft, setDraft] = useState(normalizedValue);

  useEffect(() => {
    setDraft(normalizedValue);
  }, [normalizedValue]);

  const saveDraft = () => {
    if (hexColor(draft)) onSave(draft.toLowerCase());
    else setDraft(normalizedValue);
  };

  return (
    <div style={colorInputStyle}>
      <input
        type="color"
        value={normalizedValue}
        disabled={disabled}
        onChange={(event) => onSave(event.target.value)}
        style={colorPickerStyle}
        aria-label="Color"
      />
      <input
        type="text"
        value={draft}
        disabled={disabled}
        maxLength={7}
        placeholder="#007b04"
        onChange={(event) => setDraft(event.target.value)}
        onBlur={saveDraft}
        onKeyDown={(event) => {
          if (event.key === "Enter") event.currentTarget.blur();
        }}
        style={colorTextStyle}
        aria-label="Color hex"
      />
    </div>
  );
};

const DiscountSelect = ({ value, options, disabled, onChange }) => {
  const normalizedOptions =
    value && !options.some((option) => option.code === value)
      ? [{ code: value, label: value }, ...options]
      : options;

  return (
    <select value={value || ""} disabled={disabled} onChange={(event) => onChange(event.target.value)} style={selectStyle}>
      <option value=""></option>
      {normalizedOptions.map((option) => (
        <option key={option.code} value={option.code}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

const tableHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  marginBottom: 12,
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 14,
  minWidth: 960,
};

const headerStyle = {
  padding: "10px 12px",
  textAlign: "left",
  borderBottom: "1px solid #d8dee8",
  background: "#f8fafc",
  fontWeight: 700,
};

const cellStyle = {
  padding: "10px 12px",
  borderBottom: "1px solid #edf2f7",
  verticalAlign: "middle",
};

const identityColStyle = {
  background: "#eef6ff",
};

const discountColStyle = {
  background: "#fbf7ff",
};

const groupHeaderBaseStyle = {
  padding: "8px 12px",
  borderBottom: "1px solid #d8dee8",
  fontSize: 24,
  lineHeight: "30px",
  textAlign: "center",
  color: "#ff604f",
  fontWeight: 800,
  textShadow: "0 1px 0 #fff, 0 2px 4px rgba(15, 23, 42, 0.35)",
};

const identityGroupHeaderStyle = {
  ...groupHeaderBaseStyle,
  background: "#dceeff",
};

const discountGroupHeaderStyle = {
  ...groupHeaderBaseStyle,
  background: "#f0e6ff",
  borderLeft: "3px solid #60a5fa",
};

const identityHeaderStyle = {
  ...headerStyle,
  background: "#f5faff",
};

const discountHeaderStyle = {
  ...headerStyle,
  background: "#fcf8ff",
};

const discountHeaderFirstStyle = {
  ...discountHeaderStyle,
  borderLeft: "3px solid #60a5fa",
};

const identityCellStyle = {
  ...cellStyle,
  background: "#f4f9ff",
};

const discountCellStyle = {
  ...cellStyle,
  background: "#fcf9ff",
};

const discountCellFirstStyle = {
  ...discountCellStyle,
  borderLeft: "3px solid #60a5fa",
};

const checkboxStyle = {
  width: 18,
  height: 18,
  cursor: "pointer",
};

const colorInputStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  minWidth: 150,
};

const colorPickerStyle = {
  width: 34,
  height: 34,
  padding: 2,
  border: "1px solid #cbd5e1",
  borderRadius: 4,
  background: "#fff",
};

const colorTextStyle = {
  width: 92,
  padding: "6px 8px",
  border: "1px solid #cbd5e1",
  borderRadius: 4,
  fontFamily: "monospace",
};

const selectStyle = {
  width: "100%",
  minWidth: 180,
  padding: "6px 8px",
  border: "1px solid #cbd5e1",
  borderRadius: 4,
  background: "#fff",
};

export default CustomerDirectoryTable;
